import {
  csrfHeader,
  getCsrfToken,
  MFA_REQUIRED_HEADER,
  redirectToMfaSetup,
} from "@/api/client";
import { t } from "@/i18n";

/**
 * Sign-in, the TOTP challenge and password reset, against `allauth.headless`.
 *
 * Plain `fetch`, not `client`: these are allauth's views, not DRF's, so they
 * are not in the OpenAPI schema `client` is typed from. And `client` answers a
 * 401 by navigating to the sign-in screen — which is where these calls are
 * made from, and a 401 here is an ordinary answer ("nobody is signed in",
 * "now the code, please"), not an expired session.
 *
 * The `browser` client of the headless API: same session cookie and CSRF token
 * as every other request (D10). Mounted in `config/urls.py`.
 */
const BASE = "/api/auth/browser/v1";

/** allauth's stage names, as they arrive in `data.flows[].id`. */
const FLOW_MFA = "mfa_authenticate";
const FLOW_VERIFY_EMAIL = "verify_email";

type Envelope = {
  data?: {
    flows?: { id: string; is_pending?: boolean }[];
    user?: { email?: string; display?: string };
  };
  errors?: { message: string; code: string; param?: string }[];
};

type Answer = { status: number; body: Envelope };

/** A navigation is under way; whoever awaits this has nothing left to do. */
const never = new Promise<never>(() => {});

async function call(
  method: "GET" | "POST" | "DELETE",
  path: string,
  { body, headers = {} }: { body?: unknown; headers?: Record<string, string> } = {},
): Promise<Answer> {
  try {
    // Django sets the CSRF cookie on a response, so a browser that has never
    // been here has none to echo and its first POST would be refused. Any GET
    // to the headless API sets one.
    if (method !== "GET" && !getCsrfToken()) {
      await globalThis.fetch(`${BASE}/config`, { credentials: "same-origin" });
    }
    const response = await globalThis.fetch(`${BASE}${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(method === "GET" ? {} : csrfHeader()),
        ...headers,
      },
      body: body === undefined ? null : JSON.stringify(body),
    });
    // Signed in already, on an account that still owes a second factor: this
    // is `RequireMFAMiddleware` answering, not allauth, and the only thing to
    // do about it is enrol.
    if (response.status === 403 && response.headers.has(MFA_REQUIRED_HEADER)) {
      redirectToMfaSetup();
      return await never;
    }
    // Not everything that answers is allauth: a proxy's 502 and Django's own
    // CSRF refusal are both HTML.
    const parsed: unknown = await response.json().catch(() => null);
    return {
      status: response.status,
      body: typeof parsed === "object" && parsed !== null ? (parsed as Envelope) : {},
    };
  } catch {
    // `fetch` rejects when there is no network at all.
    return { status: 0, body: {} };
  }
}

function isPending({ body }: Answer, flow: string): boolean {
  return body.data?.flows?.some((f) => f.id === flow && f.is_pending) ?? false;
}

/**
 * The sentence to put in front of the seller.
 *
 * allauth's own, when it sent one — the server speaks es-MX only
 * (`LANGUAGES`), and it is the one that knows *why* a password was refused.
 * Every error for the field is kept: a password can be too short and too
 * common at once, and fixing one to be told about the other is a bad form.
 */
function reason({ status, body }: Answer, fallback: string): string {
  if (status === 429) return t("auth.errors.rateLimited");
  const messages = (body.errors ?? []).map((error) => error.message).filter(Boolean);
  if (messages.length > 0) return messages.join(" ");
  return status === 400 ? fallback : t("auth.errors.generic");
}

export type SignInResult =
  /** Signed in. */
  | { step: "done" }
  /** The password was right; the authenticator code is owed. */
  | { step: "code" }
  /** allauth has sent a confirmation email and will not go on until it is opened. */
  | { step: "verifyEmail" }
  /** The challenge was abandoned for too long; back to the password. */
  | { step: "expired" }
  | { step: "error"; message: string };

/** True when this browser already holds a session. */
export async function isSignedIn(): Promise<boolean> {
  return (await call("GET", "/auth/session")).status === 200;
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const answer = await call("POST", "/auth/login", { body: { email, password } });
  // 409 is "you are already signed in" — from another tab, most likely.
  if (answer.status === 200 || answer.status === 409) return { step: "done" };
  if (answer.status === 401) {
    if (isPending(answer, FLOW_MFA)) return { step: "code" };
    if (isPending(answer, FLOW_VERIFY_EMAIL)) return { step: "verifyEmail" };
  }
  return { step: "error", message: reason(answer, t("auth.errors.credentials")) };
}

export async function confirmCode(code: string): Promise<SignInResult> {
  const answer = await call("POST", "/auth/2fa/authenticate", { body: { code } });
  if (answer.status === 200) return { step: "done" };
  // No stage to resume: allauth keeps a half-finished sign-in for a few
  // minutes and then forgets the password was ever typed.
  if (answer.status === 401) return { step: "expired" };
  return { step: "error", message: reason(answer, t("auth.errors.code")) };
}

/**
 * End the session. True once nobody is signed in.
 *
 * allauth answers a sign-out with 401 — "nobody is signed in" — which is the
 * outcome that was asked for, and equally what it says when the session had
 * already lapsed on its own. Anything else means the session may still be
 * there, and the screen must not pretend otherwise.
 */
export async function signOut(): Promise<boolean> {
  return (await call("DELETE", "/auth/session")).status === 401;
}

export type Result = { ok: true } | { ok: false; message: string };

/**
 * Ask for the reset email.
 *
 * `ok` says the request was taken, never that the address belongs to anyone:
 * allauth answers the same either way, on purpose, and so must the screen.
 */
export async function requestPasswordReset(email: string): Promise<Result> {
  const answer = await call("POST", "/auth/password/request", { body: { email } });
  if (answer.status === 200) return { ok: true };
  return { ok: false, message: reason(answer, t("auth.errors.generic")) };
}

export type ResetKeyCheck =
  /** `account` is whose password this is, to show above the form. */
  | { state: "valid"; account: string }
  /** Used, expired, or never a key at all. */
  | { state: "invalid" }
  /** No answer worth trusting either way. */
  | { state: "unreachable" };

/** Whether the key from an emailed link still opens anything. */
export async function checkResetKey(key: string): Promise<ResetKeyCheck> {
  const answer = await call("GET", "/auth/password/reset", {
    headers: { "X-Password-Reset-Key": key },
  });
  if (answer.status === 200) {
    const user = answer.body.data?.user;
    return { state: "valid", account: user?.email ?? user?.display ?? "" };
  }
  return { state: answer.status === 400 ? "invalid" : "unreachable" };
}

export type ResetResult =
  | { ok: true }
  /** `invalidKey`: it is the link that was refused, not the password. */
  | { ok: false; message: string; invalidKey: boolean };

export async function resetPassword(
  key: string,
  password: string,
): Promise<ResetResult> {
  const answer = await call("POST", "/auth/password/reset", {
    body: { key, password },
  });
  // 401 is the success answer: the password changed and nobody was signed in
  // by it, so the second factor is still asked for at the sign-in that follows.
  if (answer.status === 200 || answer.status === 401) return { ok: true };
  return {
    ok: false,
    message: reason(answer, t("auth.errors.generic")),
    invalidKey: answer.body.errors?.some((error) => error.param === "key") ?? false,
  };
}

/**
 * Where to go after signing in: `next` if it is a path on this origin, `/`
 * otherwise.
 *
 * `next` arrives in the query string, which anyone can write into a link.
 * Resolving it and comparing origins is what turns `//evil.example`,
 * `/\evil.example` and `javascript:` into `/` — and what is returned is the
 * parsed path, never the string that was handed in.
 */
export function safeNext(raw: string | null): string {
  if (!raw) return "/";
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return "/";
    return url.pathname + url.search + url.hash;
  } catch {
    return "/";
  }
}
